import React, { useEffect, useMemo, useState, JSX, useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCoreRowModel } from '@tanstack/react-table';

import { Button, Stack } from '@mui/material';

import { calculateCentroid } from '../../../utils/mapHelper';
import { makeNearFacilityColumn } from '../../../utils/columns/makeColumn';

import { addCarriers, deleteCarriers, getNearFacilities } from '../../../services/facility.service';
import { addGeofences, updateGeofences } from '../../../services/geofence.service';
import { addFacility, updateFacility, deleteFacility } from '../../../services/facility.service';

import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { updateTargetLocationState } from '../../../redux/slices/mapSlice';
import { updateNearFacilitieState } from '../../../redux/slices/facilitySlice';

import { Reader } from '../../../types/Reader';
import { Facility } from '../../../types/Facility';
import { GeoPosition } from '../../../types/GeoPosition';
import { CONFIRM_DIALOG_CANCEL } from '../../../constants/Constants';
import { AppPermissions } from '../../../constants/Permissions';

import withForm from '../../common/withForm';
import CustomModal from '../../common/withModal';
import { AuthGuard } from '../../common/AuthGuard';
import { useStandardTable as useTable, Table } from '../../common/table/index';

import facilityForm, { FacilityInput } from './facilityForm';
import Shimmer from '../../common/Shimmer/Shimmer';
import { AddCarrierPayload } from '../../../types/Carrier';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const FacilityForm = withForm(facilityForm);

interface NearFacilitieProp {
  reader: Reader;
  accessKey?: string;
  shouldFetch: boolean;
  onSelected: (facility: Facility) => void;
}

export default function NearFacilities(props: NearFacilitieProp): JSX.Element {
  const dispatch = useAppDispatch();
  const facilityState = useAppSelector((state) => state.facilities);
  const { notifyMessage } = useNotifyMessage();
  const [nearParamFilter, setNearParamFilter] = useState<any>({});
  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hasFetchedYet, setHasFetchedYet] = useState(false);
  const [carrierPayload, setCarrierPayload] = useState<any>({ carrierIds: [] });

  const originalCarrierIdsRef = useRef<string[]>([]);

  const forceReload = () => {
    const { location, lockedLocation } = props.reader;
    setNearParamFilter({
      lat: lockedLocation ? lockedLocation?.latitude : location?.latitude,
      lng: lockedLocation ? lockedLocation?.longitude : location?.longitude,
      distanceInMeters: 10000,
      changedAt: new Date(),
    });
  };

  const handleCarrierPayload = (carriers: AddCarrierPayload): void => {
    setCarrierPayload(carriers);
  };

  const formik = useFormik<FacilityInput>({
    initialValues: {
      id: '',
      name: '',
      stationIataCode: '',
      stationId: undefined,
      facilityType: undefined,
      latitude: '',
      longitude: '',
    },
    validationSchema: () => {
      return Yup.object<FacilityInput>({
        name: Yup.string().required('This field is required'),
        stationIataCode: Yup.string().required('This field is required'),
        latitude: Yup.string().required('This field is required'),
        longitude: Yup.string().required('This field is required'),
        facilityType: Yup.number().notRequired(),
      });
    },
    enableReinitialize: true, // Enable reinitialization
    onSubmit: async (values) => {
      const { id, name, stationIataCode, stationId, facilityType, latitude, longitude, geofences } = values;

      const payload: Facility = {
        id,
        name,
        stationIataCode,
        stationId,
        facilityType,
        location: {
          type: 'Point',
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
        },
      };

      const normalizeCarrierIds = (carrierIds: any[] = []) =>
        carrierIds.map((c) => (typeof c === 'string' ? c : (c.objectId ?? c.id ?? c.carrierId ?? ''))).filter(Boolean);

      if (id) {
        let facilityUpdated = false;

        await updateFacility(payload)
          .then(async () => {
            facilityUpdated = true;

            if (geofences && geofences.length > 0) {
              const updateGofence = { ...geofences[0] };
              const centroidMap = calculateCentroid(updateGofence?.coordinates ?? []);
              if (centroidMap) {
                updateGofence.location = {
                  type: 'Point',
                  latitude: centroidMap.lat,
                  longitude: centroidMap.lng,
                } as GeoPosition;
              }

              if (updateGofence.id) {
                await updateGeofences(updateGofence).catch(() => {
                  notifyMessage('Unable to update Geofence', 'error');
                });
              } else {
                updateGofence.facilityId = id;
                updateGofence.facilityName = name;
                updateGofence.stationIataCode = stationIataCode;
                updateGofence.stationId = stationId;
                updateGofence.name = `${stationIataCode} - ${name}`;
                updateGofence.type = 1;
                updateGofence.level = 1;

                await addGeofences(updateGofence).catch(() => {
                  notifyMessage('Unable to create Geofence', 'error');
                });
              }
            }

            notifyMessage('Update Facility success!', 'success');
          })
          .catch(() => {
            notifyMessage('Unable to update facility', 'error');
          });

        const currentIds = normalizeCarrierIds(carrierPayload?.carrierIds ?? []);
        const originalIds = originalCarrierIdsRef.current ?? [];

        const added = currentIds.filter((i) => !originalIds.includes(i));
        const removed = originalIds.filter((i) => !currentIds.includes(i));

        // Add new carriers
        if (added.length > 0) {
          try {
            await addCarriers(id, { carrierIds: added.map((aid) => ({ objectId: aid, isLocked: true })) });
            notifyMessage('Carriers added successfully!', 'success');
          } catch {
            notifyMessage('Unable to add carriers', 'error');
          }
        }

        // Remove deleted carriers
        if (removed.length > 0) {
          try {
            await deleteCarriers(id, { carrierIds: removed });
            notifyMessage('Carriers removed successfully!', 'success');
            // Update ref to avoid re-deleting
            originalCarrierIdsRef.current = originalIds.filter((x) => !removed.includes(x));
          } catch {
            notifyMessage('Unable to remove carriers', 'error');
          }
        }

        if (facilityUpdated || added.length > 0 || removed.length > 0) {
          setIsOpen(false);
          forceReload();
        }
      } else {
        await addFacility(payload)
          .then(async (res: any) => {
            const newFacilityId = res?.data?.id;

            if (geofences && geofences.length > 0 && newFacilityId) {
              const createGofence = { ...geofences[0] };
              createGofence.facilityId = newFacilityId;
              createGofence.facilityName = name;
              createGofence.stationIataCode = stationIataCode;
              createGofence.stationId = stationId;
              createGofence.name = `${stationIataCode} - ${name}`;
              createGofence.type = 1;
              createGofence.level = 1;

              const centroidMap = calculateCentroid(createGofence?.coordinates ?? []);
              if (centroidMap) {
                createGofence.location = {
                  type: 'Point',
                  latitude: centroidMap.lat,
                  longitude: centroidMap.lng,
                } as GeoPosition;
              }

              await addGeofences(createGofence).catch(() => {
                notifyMessage('Unable to create Geofence', 'error');
              });
            }

            // Add all carriers
            const normalized = normalizeCarrierIds(carrierPayload?.carrierIds ?? []);
            if (newFacilityId && normalized.length > 0) {
              try {
                await addCarriers(newFacilityId, { carrierIds: normalized.map((cid) => ({ objectId: cid, isLocked: true })) });
                notifyMessage('Carriers added successfully!', 'success');
              } catch {
                notifyMessage('Unable to add carriers', 'error');
              }
            }

            notifyMessage('Create Facility success!', 'success');
            setIsOpen(false);
            forceReload();
          })
          .catch(() => {
            notifyMessage('Unable to create facility', 'error');
          });
      }
    },
  });

  const handleEdit = (facility: Facility) => {
    dispatch(updateTargetLocationState(null));
    formik.setValues({
      id: facility.id,
      name: facility.name,
      stationIataCode: facility.stationIataCode,
      stationId: facility?.stationId ?? undefined,
      facilityType: facility.facilityType,
      latitude: facility.location.latitude.toString(),
      longitude: facility.location.longitude.toString(),
      geofences: facility?.geofences,
    });

    // Store normalized original carrier IDs
    const normalizeCarrierIds = (carrierIds: any[] = []) =>
      carrierIds.map((c) => (typeof c === 'string' ? c : (c.objectId ?? c.id ?? c.carrierId ?? ''))).filter(Boolean);

    originalCarrierIdsRef.current = normalizeCarrierIds(facility.carrierIds);
    setCarrierPayload({ carrierIds: facility.carrierIds ?? [] });

    setIsOpen(true);
  };

  const handleDelete = useCallback(
    async (id: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      await deleteFacility(id)
        .then(() => {
          notifyMessage('Facility has been removed', 'success');
          forceReload();
        })
        .catch(() => {
          notifyMessage('Unable to delete facility', 'error');
        });
    },
    [confirm, props.reader],
  );

  useEffect(() => {
    forceReload();
  }, [props.reader?.lockedLocation, props.reader.location]);

  const columns = useMemo(() => makeNearFacilityColumn(handleDelete, handleEdit), [props.reader?.centroid]);
  const table = useTable<any>({
    columns,
    fetchAsync: getNearFacilities,
    params: nearParamFilter,
    serverSide: true,
    shouldFetch: props.shouldFetch && nearParamFilter !== undefined,
    initialState: {
      pagination: {
        pageSize: 100,
        pageIndex: 0,
      },
    },
  });

  const isLoading = props.shouldFetch && !hasFetchedYet;

  const lastDataRef = useRef<any>(null);

  useEffect(() => {
    if (JSON.stringify(lastDataRef.current) !== JSON.stringify(table.data)) {
      lastDataRef.current = table.data;
      dispatch(updateNearFacilitieState(table.data));
    }
  }, [table.data]);

  useEffect(() => {
    if (!isOpen) {
      setCarrierPayload({ carrierIds: [] });
    }
  }, [isOpen]);

  useEffect(() => {
    if (props.shouldFetch && nearParamFilter && table.data) {
      setHasFetchedYet(true);
    }
  }, [table.data, props.shouldFetch, nearParamFilter]);

  const memoizedCarrierPayload = useMemo(() => carrierPayload, [carrierPayload]);

  return (
    <>
      <AuthGuard permission={AppPermissions.Facility.Edit}>
        <Stack direction='row' spacing={1} justifyContent='flex-start'>
          <Button
            onClick={() => {
              formik.resetForm();
              // create a facility (we can carry over the station (if reader.stationId) is set and geolocation (should always be set priority
              // 1) Locked Location
              // 2) Centroid
              // 3) Location
              const location = props.reader?.lockedLocation
                ? props.reader?.lockedLocation
                : props.reader?.centroid
                  ? props.reader?.centroid
                  : props.reader?.location;
              formik.setValues({
                id: '',
                name: '',
                stationIataCode: props.reader?.stationIataCode ?? '', //use stationCode default from reader
                stationId: props.reader?.stationId ?? undefined,
                facilityType: undefined,
                latitude: (location?.latitude ?? 0).toString(),
                longitude: (location?.longitude ?? 0).toString(),
                geofences: undefined,
              });

              setIsOpen(true);
            }}
            variant='contained'
            size='small'
            sx={{ ...tablePrimaryButtonSx, mb: '5px', ml: '3px' }}
          >
            Add Facility
          </Button>
        </Stack>
      </AuthGuard>
      {renderDialog()}

      <CustomModal title='Facility' isOpen={isOpen} isValid={formik.isValid} setIsOpen={setIsOpen} onConfirm={formik.handleSubmit} maxWidth='lg'>
        <FacilityForm formRef={formik} handleCarrierPayload={handleCarrierPayload} defaultCarriers={memoizedCarrierPayload} />
      </CustomModal>

      {isLoading ? (
        <Shimmer variant='rectangular' width='100%' height={35} count={12} spacing={1} />
      ) : (
        <Table
          {...table}
          getCoreRowModel={getCoreRowModel()}
          getRowProps={(row: any) => {
            return {
              style: {
                backgroundColor: row.index === hoveredRow ? 'rgb(240, 234, 229)' : '',
              },
              onMouseEnter: () => setHoveredRow(row.index),
              onMouseLeave: () => setHoveredRow(null),
              onClick: () => {
                const selectedFacility = facilityState.facilities?.find((v) => v.id === row.values.id);
                if (selectedFacility) props.onSelected(selectedFacility);
              },
            };
          }}
          showSearchBar={false}
          showColumnToggler={false}
          showAction={false}
          showSpinner={false}
        />
      )}
    </>
  );
}
